(function () {
  const CHARTS_URL = window.marbenioSizeChartsUrl;
  const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', '2XL', 'XXL', 'XXXL'];

  let chartsCache = null;

  function resolveChart(profiles, key) {
    const seen = new Set();
    let current = key || 'default';
    let merged = null;

    while (current && !seen.has(current)) {
      seen.add(current);
      const profile = profiles[current];
      if (!profile) break;

      merged = merged ? { ...merged, ...profile, sizes: profile.sizes || merged.sizes } : { ...profile };

      if (!profile.extends) {
        return merged;
      }
      current = profile.extends;
    }

    return merged || profiles.default || null;
  }

  function sizeIndex(size) {
    const idx = SIZE_ORDER.indexOf(size);
    return idx >= 0 ? idx : 0;
  }

  function harmonizeSize(sizes, availableSizes) {
    const filtered = sizes.filter(Boolean);
    if (!filtered.length) return availableSizes[0] || null;

    const indices = filtered.map(sizeIndex);
    const targetIdx = Math.max(...indices);
    let recommended = SIZE_ORDER[targetIdx] || filtered[0];

    if (availableSizes.length) {
      const availableIndices = availableSizes
        .map(sizeIndex)
        .filter((idx) => idx >= targetIdx);
      if (availableIndices.length) {
        recommended = SIZE_ORDER[Math.min(...availableIndices)];
      } else {
        recommended = availableSizes[availableSizes.length - 1];
      }
    }

    return recommended;
  }

  function computeComposite(chart, profiles, height, weight, availableSizes) {
    if (!chart?.composite?.length) return null;

    const pieces = chart.composite.map((piece) => {
      const pieceChart = resolveChart(profiles, piece.chart);
      return {
        label: piece.label,
        size: computeSize(pieceChart, height, weight, availableSizes),
      };
    });

    const recommended = harmonizeSize(
      pieces.map((piece) => piece.size),
      availableSizes
    );

    return { pieces, recommended };
  }

  function parseSizes(raw) {
    if (!raw) return [];
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }

  function loadCharts() {
    if (chartsCache) return Promise.resolve(chartsCache);
    if (!CHARTS_URL) return Promise.resolve(null);

    return fetch(CHARTS_URL)
      .then((r) => r.json())
      .then((data) => {
        chartsCache = data;
        return data;
      })
      .catch(() => null);
  }

  function computeSize(chart, height, weight, availableSizes) {
    if (!chart || !chart.sizes) return null;

    const entries = Object.entries(chart.sizes).filter(([size]) =>
      availableSizes.length ? availableSizes.includes(size) : true
    );

    if (!entries.length) return availableSizes[0] || null;

    const hW = chart.hWeight ?? 0.5;
    const wW = chart.wWeight ?? 0.5;
    const bmi = weight / Math.pow(height / 100, 2);

    let best = entries[0][0];
    let bestScore = Infinity;

    entries.forEach(([size, point]) => {
      let score = hW * Math.pow(height - point.h, 2) + wW * Math.pow(weight - point.w, 2);

      if (chart.heavyBias && bmi > 24) {
        const idx = SIZE_ORDER.indexOf(size);
        score -= chart.heavyBias * idx * 4;
      }

      if (chart.layerBias) {
        const idx = SIZE_ORDER.indexOf(size);
        score -= chart.layerBias * idx * 3;
      }

      if (chart.relaxedBias) {
        const idx = SIZE_ORDER.indexOf(size);
        score += chart.relaxedBias * idx * 2;
      }

      if (score < bestScore) {
        bestScore = score;
        best = size;
      }
    });

    return best;
  }

  function findSizeChartButton(root, productInfo) {
    const scopes = [productInfo, root].filter(Boolean);

    for (const scope of scopes) {
      const embedded = scope.querySelector('#goodchart-embedded-btn');
      if (embedded) return embedded;

      const candidates = scope.querySelectorAll('button, a[role="button"], a.button');

      for (const el of candidates) {
        const text = (el.textContent || '').trim().toLowerCase();
        if (text === 'size chart' || text === 'size guide') return el;
      }

      const legacy = scope.querySelector('button[onclick*="openSzModal"]');
      if (legacy) return legacy;

      const goodchart =
        scope.querySelector('[class*="goodchart"] button') ||
        scope.querySelector('[id*="goodchart"] button') ||
        scope.querySelector('[data-goodchart]') ||
        scope.querySelector('button[class*="GoodChart"]');

      if (goodchart) return goodchart;
    }

    return null;
  }

  function buildActionRow(chartBtn, assistRoot, productRoot, productInfo) {
    let row = productInfo.querySelector('.marbenio-size-actions');

    if (!row) {
      const trigger = assistRoot.querySelector('.marbenio-size-assist__trigger');
      if (!trigger) return;

      row = document.createElement('div');
      row.className = 'marbenio-size-actions';

      const assistSlot = document.createElement('div');
      assistSlot.className = 'marbenio-size-actions__slot marbenio-size-actions__slot--assist';
      assistSlot.appendChild(trigger);
      row.appendChild(assistSlot);

      const variantPicker = productInfo.querySelector('.product-form__input--pill, variant-selects');
      if (variantPicker && variantPicker.parentElement) {
        variantPicker.parentElement.insertBefore(row, variantPicker.nextSibling);
      } else {
        productInfo.insertBefore(row, productInfo.firstChild);
      }
    }

    if (chartBtn && !row.querySelector('.marbenio-size-actions__slot--chart')) {
      const chartSlot = document.createElement('div');
      chartSlot.className = 'marbenio-size-actions__slot marbenio-size-actions__slot--chart';

      chartBtn.classList.add('marbenio-size-chart-btn');
      const parent = chartBtn.parentElement;

      if (
        parent &&
        parent !== productInfo &&
        parent.children.length === 1 &&
        parent.tagName === 'DIV' &&
        !parent.classList.contains('marbenio-size-actions')
      ) {
        chartSlot.appendChild(parent);
      } else {
        chartSlot.appendChild(chartBtn);
      }

      row.appendChild(chartSlot);
    }

    return !!row.querySelector('.marbenio-size-actions__slot--chart');
  }

  function isGoodChartPending(productInfo, productRoot) {
    if (findSizeChartButton(productRoot, productInfo)) return false;

    const hasEmbedScript = [...document.scripts].some((script) => /goodchart/i.test(script.src));
    if (hasEmbedScript) return true;

    const embedRoot = productInfo.querySelector('#goodchart-embed-root');
    if (embedRoot && !embedRoot.querySelector('button')) return true;

    return false;
  }

  function isActionRowComplete(productInfo, productRoot, allowMissingChart) {
    const row = productInfo.querySelector('.marbenio-size-actions');
    if (!row || !row.querySelector('.marbenio-size-actions__slot--assist')) return false;

    const chartBtn = findSizeChartButton(productRoot, productInfo);
    if (chartBtn && !row.querySelector('.marbenio-size-actions__slot--chart')) return false;

    if (!allowMissingChart && isGoodChartPending(productInfo, productRoot)) return false;

    return true;
  }

  function getProductInfoRoot(sectionId) {
    return sectionId
      ? document.querySelector(`#ProductInfo-${sectionId}`)
      : document.querySelector('.product__info-container');
  }

  function findSizeRadio(root, size) {
    const normalized = size.trim().toUpperCase();
    const pillFieldsets = root.querySelectorAll('.product-form__input--pill');

    for (const fieldset of pillFieldsets) {
      const legend = fieldset.querySelector('.form__label');
      if (!legend) continue;

      const labelText = legend.textContent.toLowerCase();
      if (!labelText.includes('size') && !labelText.includes('größe') && !labelText.includes('groesse')) {
        continue;
      }

      for (const radio of fieldset.querySelectorAll('input[type="radio"]')) {
        if (radio.disabled || radio.classList.contains('disabled')) continue;
        if (radio.value.trim().toUpperCase() === normalized) return radio;
      }
    }

    return null;
  }

  function syncPurchasableState(root) {
    if (!root) return false;

    const submitBtn = root.querySelector('[name="add"]');
    const variantInput = root.querySelector('input[name="id"].product-variant-id');
    const productForm = root.querySelector('product-form');

    if (variantInput) variantInput.disabled = false;

    const variantJson = root.querySelector('variant-selects [data-selected-variant]')?.textContent;
    if (!variantJson || !productForm || !submitBtn) return !submitBtn?.disabled;

    try {
      const variant = JSON.parse(variantJson);
      if (variant?.available) {
        productForm.toggleSubmitButton(false);
        return true;
      }
      if (variant && !variant.available) {
        productForm.toggleSubmitButton(true, window.variantStrings?.soldOut);
        return true;
      }
    } catch (e) {
      /* ignore malformed variant JSON */
    }

    return !submitBtn.disabled;
  }

  function waitForVariantSync(sectionId, timeoutMs = 5000) {
    return new Promise((resolve) => {
      let settled = false;
      const root = getProductInfoRoot(sectionId);
      const cleanupFns = [];

      const settle = (ok) => {
        if (settled) return;
        settled = true;
        cleanupFns.forEach((fn) => fn());
        resolve(ok);
      };

      if (typeof subscribe !== 'undefined' && window.PUB_SUB_EVENTS) {
        const unsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (sectionId && event.data?.sectionId && String(event.data.sectionId) !== String(sectionId)) return;
          syncPurchasableState(getProductInfoRoot(sectionId));
          settle(true);
        });
        cleanupFns.push(unsub);
      }

      const poll = setInterval(() => {
        if (syncPurchasableState(getProductInfoRoot(sectionId))) settle(true);
      }, 50);
      cleanupFns.push(() => clearInterval(poll));

      const timer = setTimeout(() => {
        syncPurchasableState(getProductInfoRoot(sectionId));
        settle(false);
      }, timeoutMs);
      cleanupFns.push(() => clearTimeout(timer));
    });
  }

  function selectVariantSize(size, sectionId) {
    const root = getProductInfoRoot(sectionId);
    if (!root) return false;

    const radio = findSizeRadio(root, size);
    if (radio) {
      if (radio.checked) {
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        radio.click();
      }
      return true;
    }

    const normalized = size.trim().toUpperCase();
    const selects = root.querySelectorAll('select.select__select');
    for (const select of selects) {
      const formLabel = select.closest('.product-form__input')?.querySelector('.form__label');
      if (formLabel && !formLabel.textContent.toLowerCase().includes('size')) continue;

      for (const option of select.options) {
        if (option.disabled || option.value.trim().toUpperCase() !== normalized) continue;
        if (select.value === option.value) {
          select.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          select.value = option.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return true;
      }
    }

    return false;
  }

  class MarbenioSizeAssistant extends HTMLElement {
    connectedCallback() {
      this.chartKey = this.dataset.chartKey || 'default';
      this.availableSizes = parseSizes(this.dataset.sizes);
      this.sectionId = this.dataset.section || '';
      this.isComposite = this.dataset.composite === 'true';

      this.overlay = this.querySelector('.marbenio-size-assist__overlay');
      this.heightInput = this.querySelector('[data-height]');
      this.weightInput = this.querySelector('[data-weight]');
      this.heightValue = this.querySelector('[data-height-value]');
      this.weightValue = this.querySelector('[data-weight-value]');
      this.resultEl = this.querySelector('[data-result]');
      this.applyBtn = this.querySelector('[data-apply]');
      this.trigger = this.querySelector('.marbenio-size-assist__trigger');
      this.closeBtn = this.querySelector('.marbenio-size-assist__close');

      this.currentSize = null;
      this.chart = null;
      this.profiles = null;
      this.compositeResult = null;

      this.bindEvents();
      loadCharts().then((profiles) => {
        this.profiles = profiles || {};
        this.chart = resolveChart(this.profiles, this.chartKey);
        this.isComposite = this.isComposite || Boolean(this.chart?.composite?.length);
        this.updateRecommendation();
      });

      this.setupPlacement();
    }

    bindEvents() {
      this.trigger?.addEventListener('click', () => this.open());
      this.closeBtn?.addEventListener('click', () => this.close());
      this.overlay?.addEventListener('click', (e) => {
        if (e.target === this.overlay) this.close();
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.overlay?.classList.contains('is-open')) this.close();
      });

      const onInput = () => this.updateRecommendation();
      this.heightInput?.addEventListener('input', onInput);
      this.weightInput?.addEventListener('input', onInput);

      this.applyBtn?.addEventListener('click', () => {
        if (!this.currentSize || !this.applyBtn) return;

        const applied = selectVariantSize(this.currentSize, this.sectionId);
        if (!applied) return;

        const applyLabel = this.applyBtn.textContent;
        this.applyBtn.disabled = true;
        this.applyBtn.textContent = 'Applying…';

        waitForVariantSync(this.sectionId).finally(() => {
          this.applyBtn.disabled = false;
          this.applyBtn.textContent = applyLabel;
          this.close();
        });
      });
    }

    setupPlacement() {
      const productInfo = this.closest('.product__info-container');
      const productRoot = this.closest('.product') || productInfo;
      if (!productInfo) return;

      const finishIfComplete = (allowMissingChart) => {
        const chartBtn = findSizeChartButton(productRoot, productInfo);
        buildActionRow(chartBtn, this, productRoot, productInfo);

        if (!isActionRowComplete(productInfo, productRoot, allowMissingChart)) return false;

        this.dataset.rowBuilt = 'true';
        return true;
      };

      if (finishIfComplete(false)) return;

      const stopWatching = () => {
        observer.disconnect();
        clearInterval(pollTimer);
        clearTimeout(giveUpTimer);
      };

      const observer = new MutationObserver(() => {
        if (finishIfComplete(false)) stopWatching();
      });

      observer.observe(productRoot || document.body, { childList: true, subtree: true });

      const pollTimer = setInterval(() => {
        if (finishIfComplete(false)) stopWatching();
      }, 250);

      const giveUpTimer = setTimeout(() => {
        finishIfComplete(true);
        this.dataset.rowBuilt = 'true';
        stopWatching();
      }, 15000);
    }

    open() {
      if (!this.overlay) return;
      this.overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      if (this.overlay.parentElement !== document.body) {
        document.body.appendChild(this.overlay);
      }
    }

    close() {
      if (!this.overlay) return;
      this.overlay.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    updateRecommendation() {
      if (!this.heightInput || !this.weightInput || !this.resultEl) return;

      const height = Number(this.heightInput.value);
      const weight = Number(this.weightInput.value);

      if (this.heightValue) this.heightValue.textContent = `${height} cm`;
      if (this.weightValue) this.weightValue.textContent = `${weight} kg`;

      if (this.isComposite && this.chart && this.profiles) {
        this.compositeResult = computeComposite(
          this.chart,
          this.profiles,
          height,
          weight,
          this.availableSizes
        );
        this.currentSize = this.compositeResult?.recommended || null;

        if (this.compositeResult?.pieces?.length && this.currentSize) {
          const breakdown = this.compositeResult.pieces
            .map((piece) => `${piece.label} ${piece.size}`)
            .join('<span aria-hidden="true"> · </span>');
          this.resultEl.innerHTML = `${breakdown}<br><span class="marbenio-size-assist__result-primary">Your curated size: <strong>${this.currentSize}</strong></span>`;
          if (this.applyBtn) {
            this.applyBtn.disabled = false;
            this.applyBtn.textContent = 'Apply Curated Size';
          }
          return;
        }
      }

      this.compositeResult = null;
      this.currentSize = computeSize(this.chart, height, weight, this.availableSizes);

      if (this.currentSize) {
        this.resultEl.innerHTML = `Our suggestion for your curation: Size <strong>${this.currentSize}</strong>`;
        if (this.applyBtn) {
          this.applyBtn.disabled = false;
          this.applyBtn.textContent = 'Apply Size to Curation';
        }
      } else {
        this.resultEl.textContent = 'Adjust the sliders to discover your size.';
        if (this.applyBtn) this.applyBtn.disabled = true;
      }
    }
  }

  if (!customElements.get('marbenio-size-assistant')) {
    customElements.define('marbenio-size-assistant', MarbenioSizeAssistant);
  }
})();
